// Google Ads transforms that shape a `launcherResult` (the launch template the
// campaign launcher writes to parsed_response.json) into the objects the shared
// CLW review grid + summary pane consume. Pure data — no React, no I/O.
// Mirrors ttd/ttdRowBuilders.js and dv360/dv360RowBuilders.js so callers can
// dispatch by platform without per-platform JSX branching.
//
// Real payload shapes (campaign-services/campaign_launcher/utils/lt_*.json,
// consumed by campaign-services/g_ads_api/{search,pmax,demandgen}_campaign_creator.py):
//
//   Search — { platform: 'g-ads-search', campaigns: [{
//     name, click_url | final_url | campaign_url, daily_budget_micros,
//     total_budget_micros, start_date, end_date, country_code, path1, path2,
//     locations: [name], excluded_locations: [name], zipcodes: [zip],
//     headline_texts | headlines: [], description_texts | descriptions: [],
//     keywords: [str | {keyword, match_type}], negative_keywords: [],
//     bidding_strategy, target_cpa_micros, cpc_bid_ceiling_micros,
//     ad_groups: [{ name, match_type, max_cpc_bid_micros, keywords: [],
//                   negative_keywords: [], headlines: [], descriptions: [],
//                   final_url, path1, path2 }] }] }
//
//   PMax / Demand Gen — { platform: 'g-ads-pmax' | 'g-ads-demandgen',
//     campaigns: [{ name, daily_budget_micros, total_budget_micros,
//       is_lifetime_budget, target_roas | target_cpa, start_date, end_date,
//       location_ids: [id], final_url, assets: [asset_key],
//       targeting: { age_ranges, genders, parental_statuses, income_ranges,
//                    audience_segments, custom_audiences, interests } }],
//     assets: [{ asset_key, headlines: [], long_headline, descriptions: [],
//       business_name, logo_image, marketing_image, square_marketing_image,
//       search_themes: [], images: [], videos: [] }] }
//
// A Search plan with no `ad_groups` (the thin campaign-level template) still
// renders: the campaign itself becomes one row, carrying its campaign-level
// keywords and copy. Nothing here throws on a missing key — every field falls
// back to '—' / null so a partial plan still renders.

import { fmtMoney } from '../clwFormatters';
import {
  gadsSubtype,
  GADS_FMT,
  GADS_SUBTYPE_LABELS,
  microsToUnits,
} from './gadsConstants';

const DASH = '—';

const asArray = (v) => (Array.isArray(v) ? v : []);
const nonEmpty = (v) => (v == null ? null : (String(v).trim() || null));

/**
 * The campaign list. Most templates carry `campaigns: [...]`, but the thin
 * Search template (campaign_launcher/utils/lt_search.json) states a single
 * `campaign: {...}` — accept both so neither renders as an empty plan.
 */
const campaignsOf = (result) => {
  const list = asArray(result && result.campaigns);
  if (list.length) return list;
  const single = result && result.campaign;
  return single && typeof single === 'object' && !Array.isArray(single) ? [single] : [];
};

/**
 * Normalise the two date spellings the Google Ads templates carry
 * ('2026-08-01' and '08/01/2026') to YYYY-MM-DD. Anything else passes
 * through as a trimmed string so the grid shows what the plan actually says
 * rather than swallowing it.
 */
export function gadsYmd(value) {
  const s = nonEmpty(value);
  if (!s) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  return s;
}

// Inclusive day count between two YYYY-MM-DD dates; null when either is
// missing or unparseable. Used only to estimate a total from a daily budget.
function flightDays(start, end) {
  if (!start || !end) return null;
  const a = Date.parse(`${start}T00:00:00Z`);
  const b = Date.parse(`${end}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null;
  return Math.round((b - a) / 86400000) + 1;
}

// `· `-joined label + count summary for a list cell, matching the convention
// the TTD/DV360 builders use (grid cell shows a count, the cell detail panel
// expands the full list).
const listSummary = (items, noun) => {
  const list = asArray(items).map((v) => nonEmpty(v)).filter(Boolean);
  if (!list.length) return { label: DASH, list: DASH, count: 0 };
  const plural = list.length === 1 ? noun : `${noun}s`;
  return { label: `${list.length} ${plural}`, list: list.join(' · '), count: list.length };
};

/**
 * A Search keyword entry is either a bare string or { keyword, match_type }
 * (search_campaign_creator.py reads both). Reduce to the display text.
 */
export const keywordText = (k) => {
  if (k == null) return null;
  if (typeof k === 'string') return nonEmpty(k);
  return nonEmpty(k.keyword ?? k.text ?? k.name);
};

const keywordList = (keywords) => asArray(keywords).map(keywordText).filter(Boolean);

/**
 * A sitelink is {link_text, final_url} in the real Search templates (bare
 * strings tolerated). The cell lists the LINK TEXT — that's the label the
 * detail panel's remove/edit ops match on (see gadsEdits.applySitelinkOps).
 */
export const sitelinkText = (s) => {
  if (s == null) return null;
  if (typeof s === 'string') return nonEmpty(s);
  return nonEmpty(s.link_text ?? s.text ?? s.name);
};

// Match type for a Search row: the ad group's own value, else the first
// keyword that carries one, else '—'.
function resolveMatchType(adGroup, keywords) {
  const explicit = nonEmpty(adGroup?.match_type);
  if (explicit) return explicit.toUpperCase();
  for (const k of asArray(keywords)) {
    const mt = nonEmpty(k && typeof k === 'object' ? k.match_type : null);
    if (mt) return mt.toUpperCase();
  }
  return DASH;
}

// A `locations` entry already resolved to a Google geo target. The campaign
// copy writes these for a typed geo override so the launch targets what the
// reviewer was shown (search_campaign_creator passes them through as-is).
const PINNED_GEO_RE = /^geoTargetConstants\/(\d+)$/;

// Google's own name for a geo target, its type, and what the user typed when
// that differs: "Dubai, United Arab Emirates (City) — typed “dubai uae”".
// Without the copy's `location_targets` details it falls back to the id.
export function geoTargetLabel(target, id) {
  const parts = (nonEmpty(target?.canonical_name) || '').split(',')
    .map((p) => p.trim()).filter(Boolean)
    .filter((p, i, all) => i === 0 || p !== all[i - 1]);
  const place = parts.length ? parts.join(', ') : nonEmpty(target?.name);
  const type = nonEmpty(target?.target_type);
  const base = place ? (type ? `${place} (${type})` : place) : `Geo ${id}`;
  const same = new Set([nonEmpty(target?.name), place].filter(Boolean).map((s) => s.toLowerCase()));
  const typed = asArray(target?.typed).map((t) => nonEmpty(t)).filter(Boolean)
    .filter((t) => !same.has(t.toLowerCase()));
  return typed.length ? `${base} — typed “${typed.join('”, “')}”` : base;
}

// Campaign geo INCLUDE entries, each with the label the grid shows and the
// template slot + value it came from, so removing a label removes exactly
// what launches (gadsEdits applyGeoDeletions). `locations` carry human names
// or pinned geo targets, `location_ids` carry Google geo-target constant ids.
// A copy whose geo override was resolved carries `location_targets` (what
// each typed name matched): then every pinned target and id is shown under
// Google's name for it. Without it the names alone are shown, falling back
// to "Geo {id}" — names and ids there describe the same places.
export function gadsGeoIncludeEntries(campaign) {
  const targets = asArray(campaign?.location_targets);
  const byId = new Map(targets.map((t) => [String(t?.id ?? ''), t]));
  const label = (id) => geoTargetLabel(byId.get(id), id);
  const fromNames = asArray(campaign?.locations).map((v) => nonEmpty(v)).filter(Boolean)
    .map((value) => {
      const pinned = PINNED_GEO_RE.exec(value);
      return { slot: 'locations', value, label: pinned ? label(pinned[1]) : value };
    });
  const fromIds = asArray(campaign?.location_ids).map((v) => nonEmpty(v)).filter(Boolean)
    .map((value) => ({ slot: 'location_ids', value, label: label(value) }));
  if (targets.length) return [...fromNames, ...fromIds];
  return fromNames.length ? fromNames : fromIds;
}

// Campaign geo: include entries above, `excluded_locations` as names. Zip
// codes are surfaced separately.
function resolveGeo(campaign) {
  const exclude = asArray(campaign?.excluded_locations).map((v) => nonEmpty(v)).filter(Boolean);
  const includeAll = gadsGeoIncludeEntries(campaign).map((e) => e.label);
  const country = nonEmpty(campaign?.country_code);
  const geoIncludeList = includeAll.length ? includeAll.join(' · ') : DASH;
  const geoExcludeList = exclude.length ? exclude.join(' · ') : DASH;
  const geo = includeAll.length
    ? (exclude.length ? `${includeAll.join(' · ')} · excl. ${exclude.length}` : includeAll.join(' · '))
    : (country || DASH);
  return { geo, geoIncludeList, geoExcludeList, includeCount: includeAll.length };
}

// Bid strategy label. The templates express intent through the target fields
// rather than a strategy enum, so derive one when `bidding_strategy` is absent.
function resolveBidStrategy(campaign, subtype) {
  const explicit = nonEmpty(campaign?.bidding_strategy);
  // Raw enum spellings (MAXIMIZE_CONVERSIONS) read as machine output in the
  // configuration card; humanize while keeping already-human strings intact.
  if (explicit) {
    return /^[A-Z0-9_]+$/.test(explicit)
      ? explicit.charAt(0) + explicit.slice(1).toLowerCase().replace(/_/g, ' ')
      : explicit;
  }
  const roas = Number(campaign?.target_roas);
  if (Number.isFinite(roas) && roas > 0) return 'Maximize conversion value (tROAS)';
  const cpaMicros = campaign?.target_cpa_micros ?? campaign?.target_cpa;
  const cpa = microsToUnits(cpaMicros);
  if (cpa != null && cpa > 0) return 'Maximize conversions (tCPA)';
  if (campaign?.target_spend === true) return 'Maximize clicks (target spend)';
  return subtype === 'search' ? 'Manual CPC' : DASH;
}

// The numeric target behind the strategy, for its own column.
function resolveTargetLabel(campaign) {
  const roas = Number(campaign?.target_roas);
  if (Number.isFinite(roas) && roas > 0) return `${roas}× ROAS`;
  const cpa = microsToUnits(campaign?.target_cpa_micros ?? campaign?.target_cpa);
  if (cpa != null && cpa > 0) return `${fmtMoney(cpa, campaign?.currency_code)} CPA`;
  return DASH;
}

/**
 * Campaign-level budget in account currency.
 *   total   — total_budget_micros when the plan states one
 *   estimated — daily_budget_micros × flight days otherwise, flagged so the UI
 *               renders it with the same `~` prefix impression-only DV360
 *               plans get
 */
function resolveCampaignBudget(campaign) {
  const daily = microsToUnits(campaign?.daily_budget_micros);
  const total = microsToUnits(campaign?.total_budget_micros);
  if (total != null && total > 0) return { total, daily, estimated: false };
  const days = flightDays(gadsYmd(campaign?.start_date), gadsYmd(campaign?.end_date));
  if (daily != null && daily > 0 && days) {
    return { total: daily * days, daily, estimated: true };
  }
  return { total: null, daily, estimated: false };
}

// Asset records for PMax / Demand Gen, indexed by asset_key. A campaign
// references its asset groups by key through `assets: ['asset_key_1']`;
// pmax_campaign_creator.py also accepts an inline `asset_groups` array.
function assetIndex(result) {
  const byKey = new Map();
  for (const a of asArray(result?.assets)) {
    const key = nonEmpty(a?.asset_key);
    if (key && !byKey.has(key)) byKey.set(key, a);
  }
  return byKey;
}

// Resolve one campaign's asset groups to concrete asset records. Falls back to
// the campaign's own `asset_groups` (inline shape) and finally to a single
// synthetic group so a campaign with no assets still gets a row.
function assetGroupsFor(campaign, byKey) {
  const inline = asArray(campaign?.asset_groups);
  if (inline.length) return inline;
  const refs = asArray(campaign?.assets);
  const resolved = [];
  for (const ref of refs) {
    if (ref && typeof ref === 'object') { resolved.push(ref); continue; }
    const key = nonEmpty(ref);
    if (!key) continue;
    resolved.push(byKey.get(key) || { asset_key: key });
  }
  if (resolved.length) return resolved;
  return [{}];
}

// Image / video counts across the asset record's several spellings.
function assetMediaCounts(asset) {
  const images = new Set();
  for (const k of ['logo_image', 'marketing_image', 'square_marketing_image', 'portrait_marketing_image']) {
    const v = nonEmpty(asset?.[k]);
    if (v) images.add(v);
  }
  for (const k of ['logo_images', 'marketing_images', 'square_marketing_images', 'portrait_marketing_images', 'images']) {
    for (const v of asArray(asset?.[k])) {
      const s = nonEmpty(typeof v === 'object' ? (v.url ?? v.asset ?? v.resource_name) : v);
      if (s) images.add(s);
    }
  }
  // Videos arrive under two spellings: `videos` (Demand Gen assets, and the
  // launcher templates) and `youtube_video_ids` (what the PMax asset fetcher and
  // the copy/import adapters emit). Reading only the first reported zero videos
  // on every imported PMax campaign.
  const videos = new Set();
  for (const key of ['videos', 'youtube_video_ids', 'youtube_videos']) {
    for (const v of asArray(asset?.[key])) {
      const id = nonEmpty(typeof v === 'object' ? (v.url ?? v.id ?? v.video_id ?? v.resource_name) : v);
      if (id) videos.add(id);
    }
  }
  return { images: [...images], videos: [...videos] };
}

// Demand Gen / PMax targeting block → the audience + demographic cells.
function resolveTargeting(campaign) {
  const t = campaign?.targeting && typeof campaign.targeting === 'object' ? campaign.targeting : campaign;
  const segments = [
    ...asArray(t?.audience_segments),
    ...asArray(t?.custom_audiences),
  ].map((v) => nonEmpty(typeof v === 'object' ? (v.name ?? v.id ?? v.resource_name) : v)).filter(Boolean);
  const interests = asArray(t?.interests).map((v) => nonEmpty(typeof v === 'object' ? (v.name ?? v.id) : v)).filter(Boolean);
  const join = (arr) => {
    const list = asArray(arr).map((v) => nonEmpty(v)).filter(Boolean);
    return list.length ? list.join(', ') : DASH;
  };
  return {
    segments,
    interests,
    age: join(t?.age_ranges),
    gender: join(t?.genders),
    parental: join(t?.parental_statuses),
    income: join(t?.income_ranges),
  };
}

/**
 * Distinct creative assets the plan carries: images, videos and TEXT. Google
 * counts headlines / descriptions as assets too, and for an imported campaign
 * they are often the only creative in the payload — so a count that ignored
 * them reported 0 creative on a campaign running 14 asset groups of copy.
 *
 * Deduped by value across the whole plan: one logo reused by 14 asset groups is
 * one asset, not 14. The tile answers "what creative is in this campaign", not
 * "how many asset-to-group links exist".
 */
function gadsCreativeCounts(result, subtype) {
  const images = new Set();
  const videos = new Set();
  const texts = new Set();
  const addText = (arr) => {
    for (const v of asArray(arr)) {
      const t = nonEmpty(v && typeof v === 'object' ? v.text : v);
      if (t) texts.add(t);
    }
  };

  const byKey = assetIndex(result);
  for (const c of campaignsOf(result)) {
    if (subtype === 'search') {
      // Search creative is RSA text on the ad groups' ads, plus the
      // campaign-level seed the copy launcher writes.
      addText(c?.headline_texts);
      addText(c?.description_texts);
      for (const ag of asArray(c?.ad_groups)) {
        for (const ad of asArray(ag?.ads)) {
          addText(ad?.headline_texts);
          addText(ad?.description_texts);
        }
      }
      continue;
    }
    for (const group of assetGroupsFor(c, byKey)) {
      const media = assetMediaCounts(group);
      for (const url of media.images) images.add(url);
      for (const v of media.videos) videos.add(v);
      addText(group?.headlines ?? group?.headline_texts);
      // Long headline arrives singular from the launcher templates and plural
      // from the import / copy adapters.
      addText(group?.long_headlines);
      if (group?.long_headline) addText([group.long_headline]);
      addText(group?.descriptions ?? group?.description_texts);
    }
  }

  return {
    imageCount: images.size,
    videoCount: videos.size,
    textAssetCount: texts.size,
    creativeAssetCount: images.size + videos.size + texts.size,
  };
}

/**
 * PMax / Demand Gen audience signals, which live on the ASSET GROUP
 * (asset_group_signal → audience) rather than on the campaign. The summary only
 * counted campaign-level segments, so a PMax campaign with audience signals on
 * every asset group still reported "0 audiences targeted".
 */
function gadsAudienceSignals(result, subtype) {
  if (subtype === 'search') return [];
  const out = new Set();
  const byKey = assetIndex(result);
  for (const c of campaignsOf(result)) {
    for (const group of assetGroupsFor(c, byKey)) {
      for (const a of asArray(group?.audience_signals)) {
        const name = nonEmpty(a && typeof a === 'object'
          ? (a.name ?? a.audience ?? a.resource_name)
          : a);
        if (name) out.add(name);
      }
    }
  }
  return [...out];
}

// ── Detection ────────────────────────────────────────────────────────

/**
 * True when this launcher result is one of the three supported Google Ads
 * channels. Reads the platform field first (authoritative — every creator
 * stamps it) and falls back to shape detection for a plan whose platform
 * was dropped somewhere in the pipeline.
 */
export function isGadsLauncherResult(result) {
  if (!result || typeof result !== 'object') return false;
  if (gadsSubtype(result.platform || result.Platform)) return true;
  // Shape fallback: campaign entries carrying keys unique to the Google Ads
  // templates. Deliberately narrow — micros-denominated budgets and the
  // ad-group / asset-group structure. Generic keys like final_url or click_url
  // are NOT enough on their own; other platforms use them too.
  const campaigns = campaignsOf(result);
  if (!campaigns.length) return false;
  return campaigns.some((c) => c && (
    c.daily_budget_micros != null
    || c.total_budget_micros != null
    || Array.isArray(c.ad_groups)
    || Array.isArray(c.asset_groups)
  ));
}

/**
 * Subtype for a launcher result: the platform field when it names one, else
 * inferred from the payload shape (ad_groups → search; search themes / long
 * headline → pmax; otherwise demandgen). Defaults to 'search' when even the
 * shape is inconclusive so callers always get a usable column set.
 */
export function gadsResultSubtype(result) {
  const declared = gadsSubtype(result?.platform || result?.Platform);
  if (declared) return declared;
  const campaigns = campaignsOf(result);
  if (campaigns.some((c) => Array.isArray(c?.ad_groups) && c.ad_groups.length)) return 'search';
  const assets = asArray(result?.assets);
  if (assets.some((a) => a?.search_themes != null || a?.long_headline != null)) return 'pmax';
  if (assets.length || campaigns.some((c) => Array.isArray(c?.assets))) return 'demandgen';
  return 'search';
}

// Parse issues, if the launcher stamped any (same convention as TTD).
function gadsIssueLists(result) {
  return {
    errors: asArray(result?.templateErrors).filter(Boolean),
    warnings: asArray(result?.templateWarnings).filter(Boolean),
  };
}

// ── Summary ──────────────────────────────────────────────────────────

/**
 * Headline numbers for the summary tiles / strip. Same contract as
 * summarizeTtdResult / summarizeDv360Result / summarizeReviewCtsResult:
 * consumers read platform, ioCount, adGroupCount, totalBudget,
 * estimatedBudget, flightStart/End, campaignName, audienceCount and the
 * issue counts. Google Ads plans are currency-budgeted with no impression
 * goal, so totalImpressions / avgCpm are null by design.
 */
export function summarizeGadsResult(result) {
  if (!result) return null;
  const subtype = gadsResultSubtype(result);
  const campaigns = campaignsOf(result);
  const rows = buildGadsReviewRows(result);

  let totalBudget = 0;
  let estimated = 0;
  let sawTotal = false;
  let sawEstimate = false;
  const starts = [];
  const ends = [];
  const audiences = new Set();
  for (const c of campaigns) {
    const b = resolveCampaignBudget(c);
    if (b.total != null) {
      if (b.estimated) { estimated += b.total; sawEstimate = true; } else { totalBudget += b.total; sawTotal = true; }
    }
    const s = gadsYmd(c?.start_date);
    const e = gadsYmd(c?.end_date);
    if (s) starts.push(s);
    if (e) ends.push(e);
    for (const a of resolveTargeting(c).segments) audiences.add(a);
  }

  // Asset-group audience signals count as targeted audiences — they are what
  // PMax / Demand Gen actually target with.
  const signals = gadsAudienceSignals(result, subtype);
  for (const name of signals) audiences.add(name);
  const creative = gadsCreativeCounts(result, subtype);

  const issues = gadsIssueLists(result);
  return {
    platform: GADS_SUBTYPE_LABELS[subtype] || 'Google Ads',
    gadsSubtype: subtype,
    // Wording overrides for the shared summary tiles, which otherwise read
    // "Line items created / across N insertion orders".
    rowLabel: subtype === 'search' ? 'Ad groups parsed' : 'Asset groups parsed',
    parentLabel: 'campaigns',
    ioCount: campaigns.length,
    adGroupCount: rows.length,
    packageCount: 0,
    totalImpressions: null,
    avgCpm: null,
    totalBudget: sawTotal ? totalBudget : null,
    // A plan that states no total gets the daily × flight-days estimate,
    // surfaced through the same `estimatedBudget` field DV360 uses so the
    // header renders it with a leading `~`.
    estimatedBudget: !sawTotal && sawEstimate ? estimated : null,
    // What the estimate was derived from, so the header's caption doesn't claim
    // an impressions × CPM basis that Google Ads plans never have.
    estimatedBudgetBasis: !sawTotal && sawEstimate ? 'Estimated from daily budget × flight days' : null,
    flightStart: starts.length ? starts.sort()[0] : null,
    flightEnd: ends.length ? ends.sort().slice(-1)[0] : null,
    campaignName: nonEmpty(campaigns[0]?.name),
    advertiserId: nonEmpty(result.customer_id || result.customerId) || null,
    // The account's billing currency, carried through the import so money on
    // the summary tiles is labelled with the campaign's own symbol instead of a
    // hardcoded '$' (an INR account read as dollars).
    currencyCode: nonEmpty(campaigns.find((c) => c?.currency_code)?.currency_code) || null,
    audienceCount: audiences.size,
    // Where the audiences came from, so the tile's caption can say so rather
    // than asserting "1P/partner + custom audiences" for a PMax signal.
    audienceBasis: signals.length
      ? `${signals.length} asset-group audience signal${signals.length === 1 ? '' : 's'}`
      : null,
    ...creative,
    issueCount: issues.errors.length + issues.warnings.length,
    errorCount: issues.errors.length,
    warningCount: issues.warnings.length,
  };
}

// ── Rows ─────────────────────────────────────────────────────────────

// Fields every subtype's row carries, so the shared grid / QA / breakdown
// builders never hit an undefined key.
function baseRow(campaign, subtype, idx, campaignIdx) {
  const budget = resolveCampaignBudget(campaign);
  const geo = resolveGeo(campaign);
  const start = gadsYmd(campaign?.start_date);
  const end = gadsYmd(campaign?.end_date);
  const dailyLabel = budget.daily != null && budget.daily > 0 ? `${fmtMoney(budget.daily, campaign?.currency_code)}/day` : DASH;
  return {
    idx: idx + 1,
    // The account's billing currency rides every row so grid cells with
    // fmt 'cur' (budget, max CPC) format in it instead of assuming dollars.
    currencyCode: nonEmpty(campaign?.currency_code) || null,
    channel: 'Google Ads',
    fmt: GADS_FMT[subtype] || 'SEM',
    market: nonEmpty(campaign?.name) || DASH,
    package: nonEmpty(campaign?.name) || DASH,
    category: GADS_SUBTYPE_LABELS[subtype] || 'Google Ads',
    tactic: GADS_SUBTYPE_LABELS[subtype] || DASH,
    start,
    end,
    flightType: 'Inherits campaign',
    // Google Ads has no impression goal or CPM in the plan.
    imps: null,
    cpm: null,
    // `dailyCap` is DV360's impression cap — not a Google Ads concept. The
    // campaign's daily budget rides `dailyBudget` (currency) and
    // `dailyLabel` (the reconcile grid's "Daily $" cell reads that).
    dailyCap: null,
    dailyBudget: budget.daily,
    dailyLabel,
    pacing: campaign?.is_lifetime_budget === true ? 'Lifetime budget' : 'Daily budget',
    entityStatus: nonEmpty(campaign?.status) || DASH,
    countryCode: nonEmpty(campaign?.country_code) || DASH,
    languages: (() => {
      const l = asArray(campaign?.languages).map((v) => nonEmpty(v)).filter(Boolean);
      return l.length ? l.join(', ') : DASH;
    })(),
    geo: geo.geo,
    geoIncludeList: geo.geoIncludeList,
    geoExcludeList: geo.geoExcludeList,
    bidStrategy: resolveBidStrategy(campaign, subtype),
    targetLabel: resolveTargetLabel(campaign),
    // Campaign index travels with the row so the edit writer can address the
    // owning campaign without re-deriving it from the rowId.
    __campaignIdx: campaignIdx,
    // True when `budget` was derived from daily budget × flight days rather
    // than stated outright — the grid and breakdown tables prefix it with `~`,
    // matching how impression-only DV360 plans render.
    budgetEstimated: budget.estimated,
    // Every list field the shared builders read, defaulted.
    audiences: DASH,
    audienceList: DASH,
    keywords: DASH,
    keywordList: DASH,
    keywordCount: 0,
    interests: DASH,
    interestIds: [],
    creative: DASH,
    creativeFormat: DASH,
    freqCap: DASH,
    devices: DASH,
    environment: DASH,
    pmp: DASH,
    expansion: DASH,
    viewability: DASH,
    brandSafety: DASH,
    pixel: DASH,
    kpi: DASH,
    kpiTarget: DASH,
    convWindow: DASH,
    bidGoal: DASH,
    annotations: {},
  };
}

// One row per ad group for a Search plan. Campaign-level budget is divided
// across the campaign's ad groups the way TTD divides an IO budget across its
// ad groups, so the breakdown tables sum to the campaign total instead of
// N× it. `dailyBudget` stays un-divided — it IS the campaign's daily cap.
function searchRows(result) {
  const rows = [];
  campaignsOf(result).forEach((campaign, ci) => {
    const budget = resolveCampaignBudget(campaign);
    const adGroups = asArray(campaign?.ad_groups);
    // No ad_groups → the campaign itself is the reviewable unit.
    const units = adGroups.length ? adGroups : [null];
    const share = units.length > 0 && budget.total != null ? budget.total / units.length : null;
    const campaignKeywords = keywordList(campaign?.keywords);
    const campaignNegatives = asArray(campaign?.negative_keywords).map(keywordText).filter(Boolean);
    const campaignHeadlines = asArray(campaign?.headline_texts ?? campaign?.headlines);
    const campaignDescriptions = asArray(campaign?.description_texts ?? campaign?.descriptions);
    const campaignUrl = nonEmpty(campaign?.click_url ?? campaign?.final_url ?? campaign?.campaign_url);

    units.forEach((ag, ai) => {
      const kws = ag ? keywordList(ag.keywords) : campaignKeywords;
      const negs = ag
        ? asArray(ag.negative_keywords).map(keywordText).filter(Boolean)
        : campaignNegatives;
      const heads = ag && (ag.headlines || ag.headline_texts)
        ? asArray(ag.headlines ?? ag.headline_texts)
        : campaignHeadlines;
      const descs = ag && (ag.descriptions || ag.description_texts)
        ? asArray(ag.descriptions ?? ag.description_texts)
        : campaignDescriptions;
      const kw = listSummary(kws, 'keyword');
      const neg = listSummary(negs, 'negative');
      const head = listSummary(heads, 'headline');
      const desc = listSummary(descs, 'description');
      const zips = listSummary(campaign?.zipcodes, 'zip');
      const sitelinks = listSummary(
        asArray(ag?.sitelinks ?? campaign?.sitelinks).map(sitelinkText),
        'sitelink',
      );
      const maxCpc = microsToUnits(ag?.max_cpc_bid_micros ?? campaign?.cpc_bid_ceiling_micros);
      const name = nonEmpty(ag?.name ?? ag?.ad_group_name)
        || (adGroups.length ? `Ad group ${ai + 1}` : nonEmpty(campaign?.name) || DASH);
      const path1 = nonEmpty(ag?.path1 ?? campaign?.path1);
      const path2 = nonEmpty(ag?.path2 ?? campaign?.path2);

      rows.push({
        ...baseRow(campaign, 'search', rows.length, ci),
        // Positional rowId: the plan carries no ad-group ids before launch, so
        // the index pair is the stable address the edit writer resolves.
        rowId: `gads-${ci}-${ai}`,
        __refPath: adGroups.length ? `/campaigns/${ci}/ad_groups/${ai}` : `/campaigns/${ci}`,
        __adGroupIdx: adGroups.length ? ai : null,
        name,
        budget: share,
        // The divisor `share` was just derived from. Published so the cell
        // editor can disclose the campaign total a budget edit will write
        // (share × units) before it is committed — see GADS_SCALED_FIELDS.
        siblingCount: units.length || 1,
        matchType: resolveMatchType(ag, kws),
        keywords: kw.label,
        keywordList: kw.list,
        keywordCount: kw.count,
        negativeKeywords: neg.label,
        negativeKeywordList: neg.list,
        headlines: head.label,
        headlineList: head.list,
        descriptions: desc.label,
        descriptionList: desc.list,
        zipcodes: zips.label,
        zipcodeList: zips.list,
        sitelinks: sitelinks.label,
        sitelinkList: sitelinks.list,
        clickURL: nonEmpty(ag?.final_url) || campaignUrl || DASH,
        displayPath: path1 || path2 ? `/${[path1, path2].filter(Boolean).join('/')}` : DASH,
        path1: path1 || DASH,
        path2: path2 || DASH,
        // Search creative is the RSA copy — count headlines + descriptions so
        // the QA "creative assets" tables have a real number.
        creative: head.count + desc.count > 0
          ? `${head.count + desc.count} asset${head.count + desc.count === 1 ? '' : 's'}`
          : DASH,
        creativeFormat: head.count || desc.count
          ? `${head.count} headline${head.count === 1 ? '' : 's'} · ${desc.count} description${desc.count === 1 ? '' : 's'}`
          : DASH,
        bidFloor: maxCpc != null ? fmtMoney(maxCpc, campaign?.currency_code) : DASH,
        __maxCpc: maxCpc,
      });
    });
  });
  return rows;
}

// One row per asset group for a PMax / Demand Gen plan.
function assetGroupRows(result, subtype) {
  const byKey = assetIndex(result);
  const rows = [];
  campaignsOf(result).forEach((campaign, ci) => {
    const budget = resolveCampaignBudget(campaign);
    const groups = assetGroupsFor(campaign, byKey);
    const share = groups.length > 0 && budget.total != null ? budget.total / groups.length : null;
    const targeting = resolveTargeting(campaign);

    groups.forEach((asset, ai) => {
      const head = listSummary(asset?.headlines ?? asset?.headline_texts, 'headline');
      const desc = listSummary(asset?.descriptions ?? asset?.description_texts, 'description');
      const themes = listSummary(asset?.search_themes, 'theme');
      const media = assetMediaCounts(asset);
      const images = listSummary(media.images, 'image');
      const videos = listSummary(media.videos, 'video');
      const segs = listSummary(targeting.segments, 'audience');
      const interests = listSummary(targeting.interests, 'interest');
      const assetKey = nonEmpty(asset?.asset_key) || `asset_group_${ai + 1}`;
      const longHeadline = nonEmpty(asset?.long_headline)
        || nonEmpty(asArray(asset?.long_headlines)[0]);
      const businessName = nonEmpty(asset?.business_name)
        || nonEmpty(asArray(asset?.business_names)[0]);

      rows.push({
        ...baseRow(campaign, subtype, rows.length, ci),
        rowId: `gads-${ci}-${ai}`,
        __refPath: `/campaigns/${ci}/assets/${ai}`,
        __assetKey: nonEmpty(asset?.asset_key) || null,
        __assetGroupIdx: ai,
        // Asset groups have no name of their own in the template — the key is
        // the identity a reviewer matches against the Google Ads UI.
        name: groups.length > 1 ? `${nonEmpty(campaign?.name) || 'Campaign'} · ${assetKey}` : (nonEmpty(campaign?.name) || assetKey),
        assetKey,
        budget: share,
        // Same as searchRows: the asset-group count `share` was divided by, so
        // the editor can disclose the campaign total before commit.
        siblingCount: groups.length || 1,
        headlines: head.label,
        headlineList: head.list,
        descriptions: desc.label,
        descriptionList: desc.list,
        longHeadline: longHeadline || DASH,
        businessName: businessName || DASH,
        callToAction: nonEmpty(asset?.call_to_action_text) || DASH,
        // PMax search themes ride the `keywords` cell: they're the closest
        // analogue and the cell detail panel already expands `keywordList`.
        keywords: subtype === 'pmax' ? themes.label : DASH,
        keywordList: subtype === 'pmax' ? themes.list : DASH,
        keywordCount: subtype === 'pmax' ? themes.count : 0,
        audiences: segs.label,
        audienceList: segs.list,
        interests: interests.label,
        interestIds: targeting.interests,
        age: targeting.age,
        gender: targeting.gender,
        parental: targeting.parental,
        income: targeting.income,
        creative: images.label,
        creativeList: images.list,
        videos: videos.label,
        videoList: videos.list,
        creativeFormat: head.count || desc.count
          ? `${head.count} headline${head.count === 1 ? '' : 's'} · ${desc.count} description${desc.count === 1 ? '' : 's'}`
          : DASH,
        clickURL: nonEmpty(campaign?.final_url ?? campaign?.campaign_url ?? campaign?.click_url) || DASH,
        bidFloor: DASH,
      });
    });
  });
  return rows;
}

/**
 * One row per reviewable unit — ad group (Search) or asset group (PMax /
 * Demand Gen). Row keys follow the shared review-grid vocabulary so
 * LaunchCampaignDetails' generic builders (format breakdown, QA tables,
 * flagged items, tactic rollup) work unchanged.
 */
export function buildGadsReviewRows(result) {
  if (!result) return [];
  const subtype = gadsResultSubtype(result);
  const rows = subtype === 'search' ? searchRows(result) : assetGroupRows(result, subtype);
  return annotateGadsRows(rows, result);
}

/**
 * Attach launcher-emitted templateErrors / templateWarnings to rows as the
 * same annotations shape the other platforms carry ({ field: { type, msg } }),
 * so the Flagged Items tab and the grid's issue highlighting light up. A
 * message naming an ad group lands on that row; anything else lands on the
 * first row's campaign cell so no issue is dropped silently.
 */
function annotateGadsRows(rows, result) {
  const { errors, warnings } = gadsIssueLists(result);
  if (!rows.length || (!errors.length && !warnings.length)) return rows;

  const attach = (row, field, type, msg) => {
    const existing = row.annotations[field];
    if (!existing) row.annotations[field] = { type, msg };
    else {
      existing.extra = existing.extra || [];
      existing.extra.push({ type, msg });
    }
  };
  const place = (msg, type) => {
    const lower = String(msg).toLowerCase();
    const byRow = rows.find((r) => r.name && r.name !== DASH && lower.includes(String(r.name).toLowerCase()));
    if (byRow) { attach(byRow, 'name', type, msg); return; }
    const byCampaign = rows.find((r) => r.market && r.market !== DASH && lower.includes(String(r.market).toLowerCase()));
    if (byCampaign) { attach(byCampaign, 'market', type, msg); return; }
    attach(rows[0], 'market', type, msg);
  };
  for (const msg of errors) place(msg, 'm');
  for (const msg of warnings) place(msg, 'q');
  return rows;
}

/**
 * One row per campaign for the summary pane's allocation table — the Google
 * Ads counterpart of breakdownTtdByCampaign / breakdownDv360ByPackage.
 */
export function breakdownGadsByCampaign(result) {
  if (!result) return [];
  const subtype = gadsResultSubtype(result);
  const byKey = assetIndex(result);
  return campaignsOf(result).map((c, i) => {
    const budget = resolveCampaignBudget(c);
    const unitCount = subtype === 'search'
      ? (asArray(c?.ad_groups).length || 1)
      : assetGroupsFor(c, byKey).length;
    return {
      key: nonEmpty(c?.name) || `campaign-${i}`,
      formatCode: GADS_FMT[subtype] || 'SEM',
      packageName: nonEmpty(c?.name) || DASH,
      categoryLabel: GADS_SUBTYPE_LABELS[subtype] || 'Google Ads',
      lineItemCount: unitCount,
      impressions: 0,
      rate: null,
      budget: budget.total,
      sizes: DASH,
      flightStart: gadsYmd(c?.start_date),
      flightEnd: gadsYmd(c?.end_date),
    };
  });
}

/**
 * Campaign-level configuration card, mirroring extractTtdCampaignConfig /
 * extractDv360CampaignConfig so the summary pane's Configuration block has
 * Google-Ads-native content instead of a grid of em dashes.
 */
export function extractGadsCampaignConfig(result) {
  if (!result) return {};
  const subtype = gadsResultSubtype(result);
  const campaigns = campaignsOf(result);
  const first = campaigns[0] || {};
  const rows = buildGadsReviewRows(result);

  const geoSet = new Set();
  for (const r of rows) {
    if (r.geoIncludeList && r.geoIncludeList !== DASH) {
      for (const g of r.geoIncludeList.split(' · ')) geoSet.add(g);
    }
  }
  const geoSummary = geoSet.size === 0
    ? (nonEmpty(first.country_code) || DASH)
    : geoSet.size <= 4 ? [...geoSet].join(', ') : `${geoSet.size} locations`;

  const budget = resolveCampaignBudget(first);
  return {
    goal: resolveBidStrategy(first, subtype),
    kpi: resolveTargetLabel(first),
    freqCap: DASH,
    advertiserId: nonEmpty(result.customer_id || result.customerId) || null,
    viewabilityTarget: DASH,
    conversionPixels: 0,
    standardExclusions: 0,
    geoSummary,
    tacticSummary: GADS_SUBTYPE_LABELS[subtype] || 'Google Ads',
    formatSummary: GADS_FMT[subtype] || 'SEM',
    budgetType: first.is_lifetime_budget === true ? 'Lifetime' : 'Daily',
    topPacing: budget.daily != null ? `${fmtMoney(budget.daily, first?.currency_code)}/day` : DASH,
    // The BID STRATEGY cell showed the first ad group's max-CPC floor
    // ("$0.01") — a per-row bid bound, not the campaign's strategy. Name the
    // strategy; the numeric target already has its own KPI cell.
    topBid: resolveBidStrategy(first, subtype),
    languageSummary: rows[0]?.languages || DASH,
    deviceSummary: 'All devices',
    partnerId: null,
  };
}
